import type { BidTarget } from "@/queries/auction-session.js";
import type { Auction } from "@/queries/auction.ts";
import { currencyFormatter, numberFormatter } from "@/utils/format.ts";
import { Big } from "big.js";
import { match } from "ts-pattern";
import { type ZodType, z } from "zod";

type MaskedAmountSchemaOptions<TAllowEmpty extends boolean = false> = {
    allowEmpty?: TAllowEmpty;
    min?: number;
    max?: number;
};

type MaskedAmountSchema<TAllowEmpty extends boolean = false> = TAllowEmpty extends true
    ? z.ZodType<number | null, z.ZodTypeDef, string>
    : z.ZodType<number, z.ZodTypeDef, string>;

export const createMaskedAmountSchema = <TAllowEmpty extends boolean = false>(
    options?: MaskedAmountSchemaOptions<TAllowEmpty>,
): MaskedAmountSchema<TAllowEmpty> => {
    let baseSchema: ZodType<string, z.ZodTypeDef, string> = z
        .string()
        .regex(/^\d{1,3}(,?\d{3})*$/, "Required");

    if (options?.allowEmpty) {
        baseSchema = baseSchema.or(z.literal(""));
    }

    let schema = baseSchema.transform((value) =>
        value === "" ? null : Number.parseInt(value.replace(/,/g, ""), 10),
    ) as MaskedAmountSchema<TAllowEmpty>;

    if (!options) {
        return schema;
    }

    const { min, max } = options;

    if (min) {
        schema = schema.refine(
            (value) => value === null || value >= min,
            `Must be at least ${numberFormatter.format(min)}`,
        ) as MaskedAmountSchema<TAllowEmpty>;
    }

    if (max) {
        schema = schema.refine(
            (value) => value === null || value <= max,
            `Must be at at most ${numberFormatter.format(max)}`,
        ) as MaskedAmountSchema<TAllowEmpty>;
    }

    return schema;
};

export const getAmountFactor = (priceModel: Auction["priceModel"]): [number, number] =>
    match<Auction["priceModel"], [number, number]>(priceModel)
        .with("total", () => [1000, 1000])
        .with("per_acre", () => [100, 50])
        .exhaustive();

export const getMinimumBid = (
    target: BidTarget,
    index: number,
    config: Auction["config"],
    factor: number,
): number => {
    if (target === "global") {
        return 0;
    }

    const acreage = match(config)
        .with({ type: "single_parcel" }, (config) => {
            return config.parcel.acreage.toNumber();
        })
        .with({ type: "multi_parcel" }, (config) => {
            return config.parcels[index].acreage.toNumber();
        })
        .with({ type: "combo" }, (config) => {
            if (target === "parcel") {
                return config.parcels[index].acreage.toNumber();
            }

            if (target === "parcel_combination") {
                const combination = config.parcelCombinations[index];

                return config.parcels.reduce((acreage, parcel, index) => {
                    if (!combination.parcelIndices.includes(index)) {
                        return acreage;
                    }

                    return acreage + parcel.acreage.toNumber();
                }, 0);
            }

            return config.parcels[index].acreage.toNumber();
        })
        .with({ type: "choice" }, () => {
            return 0;
        })
        .exhaustive();

    return Math.ceil((acreage * 1000) / factor) * factor;
};

export const requireMinimumBid = (
    target: BidTarget,
    index: number,
    auction: Auction,
    factor: number,
    value: number | null,
    context: z.RefinementCtx,
    path: (string | number)[],
): void => {
    const minimum =
        auction.sealedBid || auction.priceModel === "per_acre"
            ? 1000
            : getMinimumBid(target, index, auction.config, factor);

    if (value !== null && value < minimum) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be a at least ${currencyFormatter.format(minimum)}`,
            path: path,
        });
    }
};

export const requireValidMultiple = (
    factor: number,
    value: number | null,
    context: z.RefinementCtx,
    path: (string | number)[],
): void => {
    if (value !== null && value % factor !== 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be a multiple of ${currencyFormatter.format(factor)}`,
            path: path,
        });
    }
};

export const decimalSchema = () =>
    z.string().transform((value, context) => {
        try {
            return new Big(value);
        } catch {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid decimal",
            });
            return z.NEVER;
        }
    });
